import React, { useRef } from 'react';
import styled from 'styled-components';

import { Button, Chip, Dropdown, Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { useDialog } from '~/components/Dialog/Provider';
import { PaginatedDataTable, type PaginatedDataTableRef, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import { WebhookRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

const URL = styled.div`
  white-space: nowrap;
`;

const StyledDropdown = styled(Dropdown)`
  margin: -10px 0;
`;

export const PageWebhooks: React.FC = () => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { openAlert } = useDialog();
  const navigate = useNavigate();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('webhooks');
  const tableRef = useRef<PaginatedDataTableRef>(null);

  const handleDelete = (entry: any) => {
    openAlert({
      headline: 'Delete webhook',
      content: 'This action cannot be undone.',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        if (!currentUser) {
          return;
        }
        WebhookRepository.delete(entry.id, entry.type === 'Sandbox')
          .then(() => {
            addSuccessNotification({
              content: `${entry.url} deleted`,
            });
            tableRef.current?.refetch();
          })
          .catch((error) => {
            addDangerNotification({
              content: error.message,
            });
          });
      },
    });
  };

  const handleFetch = async (params: any) => {
    return WebhookRepository.getAllList(params).then((response) => {
      const entries: any = [];
      if (response.webhookEndpoints.length) {
        response.webhookEndpoints.map((entry: any) => {
          entries.push({
            id: entry.id,
            url: entry.url,
            type: entry.type,
            status: entry.isDisabled ? 'Disabled' : 'Active',
            created: entry.createdAt,
            action: (
              <StyledDropdown
                isInside
                positionRight
                options={[
                  {
                    label: 'Edit',
                    onClick: () => navigate(`${ROUTE.PLATFORM_WEBHOOKS}/edit/${entry.type}/${entry.id}`),
                  },
                  {
                    label: 'Delete',
                    isDanger: true,
                    onClick: () => handleDelete(entry),
                  },
                ]}
                variant="dots"
              />
            ),
          });
        });
      }
      return { entries, hasMore: false };
    });
  };

  const columns: DataTableColumnDef<Record<string, any>>[] = [
    {
      header: 'URL',
      accessor: 'url',
      width: 'minmax(min-content, 1fr)',
      cell: ({ value }) => <URL>{value.replace('https://', '')}</URL>,
    },
    {
      header: 'Type',
      width: 'minmax(min-content, 140px)',
      cell: ({ value }) => <Chip icon={value === 'Production' ? <Icon.CircleCheck /> : <Icon.Sandbox />}>{value}</Chip>,
      accessor: 'type',
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 'minmax(min-content, 140px)',
      cell: ({ value }) =>
        value === 'Disabled' ? <Chip type="danger">Disabled</Chip> : <Chip type="info">Active</Chip>,
    },
    getTimestampColumn<Record<string, any>>({ accessor: 'created', timeFormat, onTimeFormatChange, width: '160px' }),
    {
      header: '',
      width: 'min-content',
      accessor: 'action',
      cell: ({ value }) => <div onClick={(event) => event.stopPropagation()}>{value}</div>,
    },
  ];

  return (
    <>
      <PageActions>
        <Button onClick={() => navigate(`${ROUTE.PLATFORM_WEBHOOKS}/edit`)} icon={<Icon.Plus />} size="small">
          Create Webhook
        </Button>
      </PageActions>

      <PaginatedDataTable<Record<string, any>>
        ref={tableRef}
        tableId="webhooks"
        fetch={(params) => handleFetch(params)}
        empty={{
          icon: <Icon.AnimationCode />,
          title: 'No webhooks',
          description: 'Add your first webhook to get started.',
          action: (
            <Button onClick={() => navigate(`${ROUTE.PLATFORM_WEBHOOKS}/edit`)} size="small" icon={<Icon.Plus />}>
              Create Webhook
            </Button>
          ),
        }}
        columns={columns}
        onRowClick={(row) => {
          navigate(`${ROUTE.PLATFORM_WEBHOOKS}/edit/${row.type}/${row.id}`);
        }}
      />
    </>
  );
};
